import { useEffect } from 'react';

// Use 'polite' for most messages. Use 'assertive' when
// visually you choose 'danger' as the message variant.
// It interrupts the user, so is for urgent errors only.
type AriaLiveTypes = 'polite' | 'assertive';

const id = 'ariaLiveRegion';

// Copypasta to hide an element without hiding it for screen readers
// Another example: https://benmyers.dev/blog/native-visually-hidden/
export const visuallyHiddenStyle: React.CSSProperties = {
  position: 'absolute',
  visibility: 'visible',
  overflow: 'hidden',
  display: 'block',
  width: '1px',
  height: '1px',
  margin: '-1px',
  border: '0',
  padding: '0',
  clip: 'rect(0px, 0px, 0px, 0px)',
  clipPath: 'polygon(0px 0px, 0px 0px, 0px 0px, 0px 0px)',
  whiteSpace: 'nowrap',
};

export default function useAriaAnnouncer() {
  // Create live region container in case it does not exist yet
  useEffect(() => {
    const liveRegion = document.getElementById(id);

    if (!liveRegion) {
      const div = document.createElement('div');
      div.id = id;
      div.ariaAtomic = 'true';
      div.ariaLive = 'off';
      Object.assign(div.style, visuallyHiddenStyle);
      document.body.appendChild(div);
    }
  }, []);

  // Return function to announce a message
  return (message: string, type: AriaLiveTypes = 'polite', retain: boolean = false) => {
    const liveRegion = document.getElementById(id);

    if (liveRegion) {
      liveRegion.ariaLive = type;
      liveRegion.innerText = message;
    }

    // If retain = false, the message is removed after 5 seconds, so that
    // when another error comes through with the same text, it is announced.
    // Otherwise it sees no change in text, and the live region stays quiet.
    if (!retain) {
      setTimeout(() => {
        if (liveRegion) {
          liveRegion.innerText = '';
        }
      }, 5000);
    }
  };
}
